/**
 * Chasse aux indices version LIVE — autonome (pas de context React global).
 *
 * Différences avec la version V2 « exercice tuteur » :
 *   - Pas de chrono propre (le chrono est celui du step Live, 30 s).
 *   - Interactivité gatée par `disabled` : quand l'étape est revealed (passée)
 *     ou answered, on ne peut plus cliquer, seuls les indices trouvés restent
 *     visibles.
 *   - État local (revealed Set, wrongClicks) — la XP est envoyée au parent via
 *     `onFound(clueId, xp)` pour être injectée dans le score du step.
 *   - Rendu inline (accordéon comme la V2), overlays verts + badge XP flottant
 *     réutilisent les helpers purs `@jeraisonne/db/clue-hunt`.
 *
 * L'anti-triche reste CLIENT (comme la V2) : les clues et leurs revealed_md
 * sont dans le payload chiffré déchiffré côté client. À durcir plus tard
 * si besoin (une edge fn `submit-live-clue-hunt` séparée).
 */

import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { ChevronDown, Search } from 'lucide-react';
import { MarkdownMath, cn } from '@jeraisonne/ui';
import {
  applyClueFound,
  applyWrongClick,
  findClueByClick,
  initialClueHuntState,
  normalizeClueMatch,
  type Clue,
  type ClueHuntState,
} from '@jeraisonne/db';
import type { LiveClueHunt } from './use-live-quiz';

export interface ClueHuntLiveProps {
  data: LiveClueHunt;
  /** true pour un step déjà revealed (passé) : mode read-only, pas d'interactions. */
  disabled?: boolean;
  /** Callback à chaque indice trouvé/révélé, avec la XP effective créditée. */
  onFound?: (clueId: string, xp: number) => void;
  /** Appelé UNE fois quand TOUS les indices sont révélés, avec la XP totale glanée. */
  onComplete?: (earnedXp: number) => void;
  /** Ref du wrapper englobant le contenu du step (statement + question) — sert
      de scope pour capturer les clics sur les mots. */
  scopeRef: React.RefObject<HTMLElement | null>;
}

/** Convertit la LiveClue (snake_case) vers le format Clue utilisé par @jeraisonne/db. */
function toClue(c: LiveClueHunt['clues'][number]): Clue {
  return {
    id: c.id,
    source_text: c.source_text,
    revealed_md: c.revealed_md,
    xp_reward: c.xp_reward,
  };
}

export function ClueHuntLive({
  data,
  disabled = false,
  onFound,
  onComplete,
  scopeRef,
}: ClueHuntLiveProps) {
  const clues = useMemo(() => data.clues.map(toClue), [data.clues]);
  const [state, setState] = useState<ClueHuntState>(() => initialClueHuntState());
  const [expanded, setExpanded] = useState(true);

  // Fin de chasse : dès que TOUS les indices sont révélés, on notifie une seule
  // fois le parent avec la XP totale glanée (→ popup de feedback avant la question).
  const completedRef = useRef(false);
  useEffect(() => {
    if (completedRef.current) return;
    if (clues.length === 0 || state.revealed.size < clues.length) return;
    completedRef.current = true;
    let earned = 0;
    for (const v of state.earned.values()) earned += v;
    onComplete?.(earned);
  }, [state.revealed, state.earned, clues.length, onComplete]);
  const [floatingXp, setFloatingXp] = useState<{
    key: string;
    x: number;
    y: number;
    xp: number;
  } | null>(null);

  const revealedClues = clues.filter((c) => state.revealed.has(c.id));

  // Repli auto quand le step est disabled (question répondue / passée).
  useEffect(() => {
    if (disabled) setExpanded(false);
  }, [disabled]);

  // ─── Délégation d'événement sur le scope ─────────────────────────────────
  useEffect(() => {
    if (disabled) return;
    const el = scopeRef.current;
    if (!el) return;
    function handler(evt: MouseEvent) {
      const t = evt.target as Element | null;
      if (t?.closest('button,input,select,textarea,a')) return;
      const word = extractClickedText(evt);
      if (!word) return;
      handleWord(word, evt.clientX, evt.clientY);
    }
    el.addEventListener('click', handler);
    return () => el.removeEventListener('click', handler);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [disabled, scopeRef, clues]);

  const handleWord = useCallback(
    (text: string, x: number, y: number) => {
      setState((prev) => {
        const clue = findClueByClick(text, clues, prev.revealed);
        if (clue) {
          const next = applyClueFound(prev, clue);
          const xp = next.earned.get(clue.id) ?? 0;
          if (xp > 0) setFloatingXp({ key: `${clue.id}-${Date.now()}`, x, y, xp });
          if (onFound) onFound(clue.id, xp);
          return next;
        }
        const { state: nextState, revealed } = applyWrongClick(prev, clues);
        if (revealed && onFound) onFound(revealed.id, 0);
        return nextState;
      });
    },
    [clues, onFound],
  );

  return (
    <>
      <section
        className="rounded-2xl border-2 border-cyan-200 bg-cyan-50/50"
        aria-label="Chasse aux indices"
      >
        <button
          type="button"
          onClick={() => setExpanded((v) => !v)}
          aria-expanded={expanded}
          className="flex w-full items-start gap-2 rounded-t-2xl px-4 py-3 text-left transition-colors hover:bg-cyan-100/60 active:scale-[0.99]"
        >
          <Search className="mt-0.5 h-4 w-4 shrink-0 text-cyan-700" />
          <span className="text-ink-900 flex-1 text-sm font-semibold">
            <MarkdownMath>{data.intro_md}</MarkdownMath>
          </span>
          <ChevronDown
            className={cn(
              'mt-0.5 h-4 w-4 shrink-0 text-cyan-700 transition-transform',
              expanded ? 'rotate-180' : '',
            )}
          />
        </button>
        {expanded && (
          <div className="px-4 pb-3">
            {revealedClues.length === 0 ? (
              <p className="text-ink-500 text-xs italic">
                {disabled
                  ? "Aucun indice trouvé pendant l'étape."
                  : "Clique sur les données importantes dans l'énoncé pour les révéler."}
              </p>
            ) : (
              <ol className="space-y-1.5">
                {revealedClues.map((clue) => {
                  const xp = state.earned.get(clue.id);
                  return (
                    <li
                      key={clue.id}
                      className="flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-2.5 py-1.5 text-sm"
                    >
                      <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-emerald-500 text-white">
                        <Search className="h-3.5 w-3.5" />
                      </span>
                      <span className="min-w-0 flex-1">
                        <span className="text-ink-900 font-medium">
                          <MarkdownMath>{clue.revealed_md}</MarkdownMath>
                        </span>
                      </span>
                      {xp !== undefined && (
                        <span
                          className={cn(
                            'shrink-0 rounded-full px-2 py-0.5 text-[10px] font-semibold',
                            xp > 0
                              ? 'bg-emerald-200 text-emerald-900'
                              : 'bg-amber-200 text-amber-900',
                          )}
                        >
                          {xp > 0 ? `+${xp}` : '0'}
                        </span>
                      )}
                    </li>
                  );
                })}
              </ol>
            )}
          </div>
        )}
      </section>

      {/* Encadrés verts sur les indices trouvés dans le scope. */}
      <ClueTargets scopeRef={scopeRef} clues={clues} revealed={state.revealed} />

      {/* Floating XP badge — pareil que V2 (montée + fade). */}
      {floatingXp && (
        <FloatingXpBadge
          key={floatingXp.key}
          x={floatingXp.x}
          y={floatingXp.y}
          xp={floatingXp.xp}
          onDone={() => setFloatingXp(null)}
        />
      )}
    </>
  );
}

// ─── Overlays des indices trouvés ───────────────────────────────────────────

function ClueTargets({
  scopeRef,
  clues,
  revealed,
}: {
  scopeRef: React.RefObject<HTMLElement | null>;
  clues: Clue[];
  revealed: Set<string>;
}) {
  const [rects, setRects] = useState<Array<{ x: number; y: number; w: number; h: number }>>([]);

  useLayoutEffect(() => {
    const scope = scopeRef.current;
    if (!scope) return;

    function compute() {
      const el = scopeRef.current;
      if (!el) return;
      const scopeRect = el.getBoundingClientRect();
      const out: Array<{ x: number; y: number; w: number; h: number }> = [];
      for (const clue of clues) {
        if (!revealed.has(clue.id)) continue;
        // Seulement la PREMIÈRE occurrence (ordre DOM), pas toutes les répétitions.
        const r = findTextRects(el, clue.source_text, 1)[0];
        if (r) {
          out.push({
            x: r.left - scopeRect.left,
            y: r.top - scopeRect.top,
            w: r.width,
            h: r.height,
          });
        }
      }
      setRects(out);
    }

    compute();
    const ro = new ResizeObserver(compute);
    ro.observe(scope);
    const mo = new MutationObserver(compute);
    mo.observe(scope, { childList: true, subtree: true, characterData: true });
    window.addEventListener('scroll', compute, true);
    window.addEventListener('resize', compute);
    return () => {
      ro.disconnect();
      mo.disconnect();
      window.removeEventListener('scroll', compute, true);
      window.removeEventListener('resize', compute);
    };
  }, [scopeRef, clues, revealed]);

  return (
    <>
      {rects.map((r, i) => (
        <div
          key={i}
          className="pointer-events-none absolute rounded-md bg-emerald-200/30 shadow-[0_1px_5px_rgba(16,185,129,0.45)] transition-all duration-200"
          style={{ left: r.x - 2, top: r.y - 2, width: r.w + 4, height: r.h + 4 }}
          aria-hidden
        />
      ))}
    </>
  );
}

// ─── Floating XP badge (identique V2) ───────────────────────────────────────

function FloatingXpBadge({
  x,
  y,
  xp,
  onDone,
}: {
  x: number;
  y: number;
  xp: number;
  onDone: () => void;
}) {
  useEffect(() => {
    const t = setTimeout(onDone, 1200);
    return () => clearTimeout(t);
  }, [onDone]);
  return (
    <div
      role="status"
      aria-live="polite"
      className="pointer-events-none fixed z-[80] -translate-x-1/2 -translate-y-full"
      style={{ left: `${x}px`, top: `${y}px` }}
    >
      <div
        className={cn(
          'clue-live-xp-anim inline-flex items-center gap-1 rounded-full px-3 py-1 text-sm font-extrabold shadow-lg',
          'bg-gradient-to-br from-emerald-400 to-emerald-600 text-white',
        )}
      >
        <span>+{xp} XP</span>
      </div>
      <style>{`
        @keyframes clue-live-xp-float {
          0% { transform: translateY(0) scale(0.7); opacity: 0; }
          15% { transform: translateY(-8px) scale(1.1); opacity: 1; }
          30% { transform: translateY(-16px) scale(1); opacity: 1; }
          100% { transform: translateY(-56px) scale(0.95); opacity: 0; }
        }
        .clue-live-xp-anim {
          animation: clue-live-xp-float 1200ms cubic-bezier(0.22, 0.61, 0.36, 1) forwards;
        }
      `}</style>
    </div>
  );
}

// ─── Helpers : extraction et matching ──────────────────────────────────────

function extractClickedText(evt: MouseEvent): string | null {
  const t = evt.target as Element | null;
  const katexRoot = t?.closest('.katex') as HTMLElement | null;
  if (katexRoot) {
    const annotation = katexRoot.querySelector('annotation[encoding="application/x-tex"]');
    const src = annotation?.textContent?.trim();
    if (src) return src;
    return katexRoot.textContent?.trim() || null;
  }
  const caret = resolveCaret(evt.clientX, evt.clientY);
  if (!caret) return null;
  const raw = caret.node.textContent ?? '';
  if (!raw) return null;
  return wordAround(raw, caret.offset);
}

function resolveCaret(x: number, y: number): { node: Text; offset: number } | null {
  const dAny = document as unknown as {
    caretPositionFromPoint?: (x: number, y: number) => { offsetNode: Node; offset: number } | null;
    caretRangeFromPoint?: (x: number, y: number) => Range | null;
  };
  const pos = dAny.caretPositionFromPoint?.(x, y);
  if (pos && pos.offsetNode.nodeType === Node.TEXT_NODE)
    return { node: pos.offsetNode as Text, offset: pos.offset };
  const r = dAny.caretRangeFromPoint?.(x, y);
  if (r && r.startContainer.nodeType === Node.TEXT_NODE)
    return { node: r.startContainer as Text, offset: r.startOffset };
  return null;
}

function wordAround(raw: string, offset: number): string | null {
  if (offset < 0 || offset > raw.length) return null;
  const isWord = (ch: string) => /[\p{L}\p{N}_+\-=.,°%×÷/]/u.test(ch);
  let start = offset;
  while (start > 0 && isWord(raw[start - 1]!)) start -= 1;
  let end = offset;
  while (end < raw.length && isWord(raw[end]!)) end += 1;
  return raw.slice(start, end).trim() || null;
}

function findTextRects(root: HTMLElement, needle: string, limit: number): DOMRect[] {
  const norm = normalizeClueMatch(needle);
  if (!norm) return [];
  const out: DOMRect[] = [];
  const walker = document.createTreeWalker(root, NodeFilter.SHOW_TEXT, {
    acceptNode(node: Node) {
      if (!node.textContent || !node.textContent.trim()) return NodeFilter.FILTER_REJECT;
      const el = (node as Text).parentElement;
      if (el?.closest('.katex-mathml')) return NodeFilter.FILTER_REJECT;
      return NodeFilter.FILTER_ACCEPT;
    },
  });
  let node: Node | null = walker.currentNode;
  while ((node = walker.nextNode())) {
    if (out.length >= limit) break;
    const text = (node as Text).textContent ?? '';
    if (!normalizeClueMatch(text).includes(norm)) continue;
    const low = text.toLowerCase();
    const target = needle.trim().toLowerCase();
    let from = 0;
    while (from < low.length && out.length < limit) {
      const idx = low.indexOf(target, from);
      if (idx < 0) break;
      try {
        const range = document.createRange();
        range.setStart(node, idx);
        range.setEnd(node, idx + target.length);
        const rect = range.getBoundingClientRect();
        if (rect.width > 0 && rect.height > 0) out.push(rect);
      } catch {
        /* offset invalide */
      }
      from = idx + target.length;
    }
  }
  if (out.length < limit) {
    const annos = root.querySelectorAll<HTMLElement>(
      '.katex annotation[encoding="application/x-tex"]',
    );
    for (const a of annos) {
      if (out.length >= limit) break;
      const src = a.textContent?.trim() ?? '';
      if (!src) continue;
      const normSrc = src.replace(/\s+/g, '').toLowerCase();
      const normNeedle = needle.replace(/\s+/g, '').toLowerCase();
      if (!normSrc.includes(normNeedle)) continue;
      const katexRoot = a.closest('.katex') as HTMLElement | null;
      if (!katexRoot) continue;
      const rect = katexRoot.getBoundingClientRect();
      if (rect.width > 0 && rect.height > 0) out.push(rect);
    }
  }
  return out;
}
